import { SwiperOptions } from 'swiper/types';
import { pipe, join, compact } from '@fxts/core';

export type SwiperPluginProps = {
  /**
   * @default true
   *
   * @description
   * - Swiper plugin 활성화 여부를 설정합니다.
   * - 객체가 존재하면 기본적으로 활성화됩니다.
   *
   *
   * @example1 기본 사용 (enabled: true와 동일)
   * ```ts
   * new ProductCardListPluginHelper({
   *   [ProductCardListPlugin.swiper]: {
   *     width: 200,
   *   }
   * })
   * ```
   *
   * @example2 명시적으로 활성화
   * ```ts
   * new ProductCardListPluginHelper({
   *   [ProductCardListPlugin.swiper]: {
   *     enabled: true,
   *     width: 200,
   *   }
   * })
   * ```
   *
   *
   * @example3 비활성화
   * ```ts
   * new ProductCardListPluginHelper({
   *   [ProductCardListPlugin.swiper]: {
   *     enabled: false,
   *     width: 200,
   *   }
   * })
   * ```
   */
  enabled?: boolean;

  /**
   * swiper.js 옵션 주입
   */
  swiperOptions: SwiperOptions;

  /**
   * 상품 카드 하나의 width (px)
   */
  width: number;

  /**
   * 상품 카드 사이 간격 (px)
   */
  gap?: number;
};

export enum ProductCardListPlugin {
  swiper = 'swiper',
}

export type ProductCardListPluginProps = {
  [ProductCardListPlugin.swiper]?: SwiperPluginProps;
};

type PluginsWithSwiper = ProductCardListPluginProps & {
  [ProductCardListPlugin.swiper]: SwiperPluginProps;
};

export class ProductCardListPluginHelper {
  private readonly swiperPlugin?: SwiperPluginProps;

  constructor(plugins?: ProductCardListPluginProps) {
    if (ProductCardListPluginHelper.isSwiperEnabled(plugins)) {
      this.swiperPlugin = plugins[ProductCardListPlugin.swiper];
    }
  }

  static isSwiperEnabled(plugins?: ProductCardListPluginProps): plugins is PluginsWithSwiper {
    return !!(
      plugins &&
      plugins[ProductCardListPlugin.swiper] &&
      plugins[ProductCardListPlugin.swiper].enabled !== false
    );
  }

  getSwiperPlugin(): SwiperPluginProps | undefined {
    return this.swiperPlugin;
  }

  static getSwiperPlugin(plugins?: ProductCardListPluginProps): SwiperPluginProps | undefined {
    return this.isSwiperEnabled(plugins) ? plugins[ProductCardListPlugin.swiper] : undefined;
  }

  getSwiperStyleVars(): string {
    const plugin = this.getSwiperPlugin();

    const styles: string[] = [];
    if (plugin) {
      styles.push(`--swiper-plugin-width: ${plugin.width}px`);
      if (plugin.gap) {
        styles.push(`--swiper-plugin-column-gap: ${plugin.gap}px`);
      }
      return pipe(styles, compact, join(';'));
    } else {
      return '';
    }
  }
}
